// PRD 16 LR11c (register E38 (2)): a participant's identity in the Control Panel — the key the api asks (common/childParticipant
// `participantIdentity`) and SportyaOS keeps (`drawIdentity.ts`). A Tenis10 child rides on the parent's account: two siblings
// share `userId`, so a child is its `profileId` and an adult its `userId`. Every "same participant?" question of the draw and
// match dialogs — who is placed, who is chosen, who can be swapped — asks this key, never `userId` alone.
import { childNumber } from "pages/Tenis10Claims/claimsLabels"

const hasValue = value => value !== undefined && value !== null && value !== ""

/** The key of a slot's or an entry's participant, or `null` for a BYE, pending or empty place. */
export const participantKey = slot => {
  if (!slot || typeof slot !== "object") return null
  if (hasValue(slot.profileId)) return `profile:${slot.profileId}`
  if (hasValue(slot.userId)) return `user:${slot.userId}`
  return null
}

/** The number a participant shows ("#id"): an adult's userId; a child's own number (RELEASE-TENIS10 § 11 P78, `childId` flat
 * on a draw or match slot, beside `profileId`) — never its userId, which is its parent's; none while the read carries none. */
export const accountNumber = person => hasValue(person?.profileId) ? childNumber(person)
  : hasValue(person?.userId) ? person.userId : null

/** How a participant whose read carries no name reads: a child by its own number, or as a child — never „Player #<userId>”, the
 * parent's number its siblings share; an adult by its account number; `null` when nothing identifies it. */
export const unnamedParticipant = person => hasValue(person?.profileId)
  ? childNumber(person) != null ? `Child #${childNumber(person)}` : "Tenis10 child"
  : hasValue(person?.userId) ? `Player #${person.userId}` : null

/** The people of a Main List entry: its child, or its player and doubles partner (by id or by their snapshots). */
export const entryKeys = entry => hasValue(entry?.profileId)
  ? [participantKey(entry)]
  : [entry?.userId ?? entry?.user?.userId, entry?.partnerId ?? entry?.partner?.userId].filter(hasValue).map(userId => `user:${userId}`)

/** The keys of every participant placed in matches, at any nesting (groups, phases). */
export const placedKeys = (matches = []) => matches
  .flat(Infinity)
  .flatMap(match => [match?.player, match?.opponent, match?.playerPartner, match?.opponentPartner].map(participantKey))
  .filter(Boolean)

/** Every person of the entry is placed (an entry with nobody to place never is). */
export const isPlaced = (entry, placed) => {
  const keys = entryKeys(entry)
  return keys.length > 0 && keys.every(key => placed.includes(key))
}

/**
 * The slot a match write sends for an entry: an adult's account snapshot, as before; a child's name, photo and `profileId`
 * on the parent's account — the api keeps none of the parent's account data on it (Events `participantIdentity`), and the
 * parent's photo is never the child's. RELEASE-TENIS10 § 11 P78: the child's own number too, flat beside `profileId` (from the
 * entry's `profile.childId`, or a slot's flat `childId`) — the api keeps on a match slot only what the write sends.
 */
export const entrySlot = entry => {
  if (!hasValue(entry?.profileId)) return entry?.user
  const { profilePicture, ...parent } = entry.user || {}
  const childId = entry.profile?.childId ?? entry.childId
  return {
    ...parent,
    userId: parent.userId ?? entry.userId,
    profileId: entry.profileId,
    ...(hasValue(childId) && { childId }),
    firstName: entry.profile?.firstName ?? parent.firstName,
    lastName: entry.profile?.lastName ?? parent.lastName,
    ...(entry.profile?.profilePicture && { profilePicture: entry.profile.profilePicture }),
  }
}
